import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/react";
import { toast } from "sonner";
import { Check, Minus, Plus, Loader2, Lock, ArrowLeft, ArrowRight, Info, Ruler, Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { api, errMsg, money, imgUrl, useAdminApi } from "../lib/api";
import { useLang, svcName, useTitle } from "../lib/i18n";
import { useConfig } from "../lib/config";
import SlotPicker, { fmtTime } from "../components/booking/SlotPicker";

const Stepper = ({ value, onChange, min = 0, max = 10, step = 1, testid }) => (
  <div className="inline-flex items-center rounded-xl border border-slate-300">
    <button type="button" className="p-2.5 hover:bg-slate-50" onClick={() => onChange(Math.max(min, value - step))} data-testid={`${testid}-minus`}><Minus size={16} /></button>
    <span className="w-12 text-center font-semibold" data-testid={`${testid}-value`}>{value}</span>
    <button type="button" className="p-2.5 hover:bg-slate-50" onClick={() => onChange(Math.min(max, value + step))} data-testid={`${testid}-plus`}><Plus size={16} /></button>
  </div>
);

function Summary({ price, loading, service, hold, form }) {
  const { t, lang } = useLang();
  const due = price ? (form.payment_option === "deposit" ? price.deposit_amount : price.total) : 0;
  const L = ({ k, v, neg }) => <div className="flex justify-between text-sm"><span className="text-slate-300">{k}</span><span className={neg ? "text-emerald-300" : ""}>{neg ? "-" : ""}{money(v)}</span></div>;
  return (
    <aside className="lg:col-span-4 bg-slate-900 text-white p-6 rounded-2xl shadow-xl h-fit lg:sticky lg:top-24 space-y-4" data-testid="live-price-summary">
      <div className="flex items-center justify-between"><h3 className="font-semibold">{t.book.summary}</h3>{loading && <Loader2 size={16} className="animate-spin text-sky-300" />}</div>
      {service && <p className="text-sky-300 font-medium">{svcName(service, lang)}</p>}
      {hold && <p className="text-sm text-slate-300">{hold.date} · {fmtTime(hold.time)}</p>}
      {price ? (
        <div className="space-y-2 border-t border-slate-700 pt-4">
          {price.lines.map((l) => <L key={l.label} k={l.label} v={l.amount} />)}
          {price.addons.map((a) => <L key={a.addon_id} k={`${lang === "es" ? a.name_es : a.name_en}${a.quantity > 1 ? ` x${a.quantity}` : ""}`} v={a.amount} />)}
          {price.frequency_discount > 0 && <L k={`${t.book.discount} (${price.frequency_discount_percent}%)`} v={price.frequency_discount} neg />}
          {price.surcharges.map((s) => <L key={s.label} k={s.label} v={s.amount} />)}
          {price.travel_fee > 0 && <L k={t.book.travel} v={price.travel_fee} />}
          {price.coupon_discount > 0 && <L k={`${t.book.couponLabel} ${price.coupon_code}`} v={price.coupon_discount} neg />}
          <div className="flex justify-between border-t border-slate-700 pt-3 text-lg font-bold"><span>{t.book.total}</span><span data-testid="live-price-total">{money(price.total)}</span></div>
          <div className="flex justify-between text-sm text-sky-300"><span>{t.book.dueNow}</span><span data-testid="live-price-due-now">{money(due)}</span></div>
          {price.estimated_hours && (
            <div className="flex items-start gap-2 rounded-xl bg-slate-800 p-3 text-sm" data-testid="estimated-time">
              <Clock size={16} className="text-sky-300 mt-0.5" />
              <div><p className="font-semibold">{t.book.estTime}: <span data-testid="estimated-hours">{price.estimated_hours} {t.book.hours}</span></p><p className="text-xs text-slate-400">{t.book.estNote}</p></div>
            </div>
          )}
        </div>
      ) : <p className="text-sm text-slate-400">—</p>}
    </aside>
  );
}

const EMPTY = { service_id: "", property_type: "house", bedrooms: 2, bathrooms: 1, sqft: 1000, frequency: "one_time", addons: {},
  line1: "", line2: "", city: "Hialeah", zip: "", access: "", name: "", email: "", phone: "", notes: "", payment_option: "full" };

export default function Book({ admin = false }) {
  const { t, lang } = useLang();
  const cfg = useConfig();
  useTitle(t.book.title);
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...EMPTY, service_id: params.get("service") || "" });
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState("");
  const [hold, setHold] = useState(null);
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const timer = useRef();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const { user } = useUser();
  const call = useAdminApi();
  const navigate = useNavigate();
  const [offline, setOffline] = useState({ method: "cash", amount: "", reference: "" });
  useEffect(() => {
    if (user && !admin) setForm((f) => ({ ...f, name: f.name || user.fullName || "", email: f.email || user.primaryEmailAddress?.emailAddress || "" }));
  }, [user, admin]);
  const service = useMemo(() => cfg?.services.find((s) => s.id === form.service_id), [cfg, form.service_id]);
  const fixed = (cfg?.services || []).filter((s) => admin || s.pricing_model === "fixed");
  const [custom, setCustom] = useState({ price: "", hours: "" });
  const isQuote = service && service.pricing_model !== "fixed";
  const customOk = Number(custom.price) > 0 && Number(custom.hours) >= 1;
  const addonList = Object.entries(form.addons).filter(([, q]) => q > 0).map(([id, qty]) => ({ id, qty }));

  const payload = () => ({ service_id: form.service_id, bedrooms: form.bedrooms, bathrooms: form.bathrooms, sqft: form.sqft, frequency: form.frequency,
    addons: addonList, zip: /^\d{5}$/.test(form.zip) ? form.zip : "", date: hold?.date || null, coupon_code: couponApplied || null });

  useEffect(() => {
    if (!service || (isQuote && !(admin && customOk))) { setPrice(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        setPrice(admin ? await call("post", "/admin/pricing/calculate", { ...payload(), ...(isQuote ? { custom_price: Number(custom.price), custom_hours: Number(custom.hours) } : {}) })
          : (await api.post("/pricing/calculate", payload())).data);
      }
      catch (e) { toast.error(errMsg(e)); if (couponApplied) setCouponApplied(""); }
      finally { setLoading(false); }
    }, 300);
  }, [form.service_id, form.bedrooms, form.bathrooms, form.sqft, form.frequency, JSON.stringify(form.addons), form.zip, hold?.date, couponApplied, custom.price, custom.hours]); // eslint-disable-line react-hooks/exhaustive-deps

  const [size, setSize] = useState(null);
  const [sqftTouched, setSqftTouched] = useState(false);
  const estimate = (beds, baths) => Math.round((500 + 350 * beds + 150 * baths) / 50) * 50;
  useEffect(() => {
    if (size?.source === "estimate" && !sqftTouched) set("sqft", estimate(form.bedrooms, form.bathrooms));
  }, [form.bedrooms, form.bathrooms, size, sqftTouched]); // eslint-disable-line react-hooks/exhaustive-deps

  const lookupSize = async () => {
    const key = `${form.line1}|${form.city}|${form.zip}`.toLowerCase();
    if (size?.key === key) return;
    setSize({ key, source: "loading" });
    try {
      const r = (await api.post("/property/lookup", { line1: form.line1, city: form.city, zip: form.zip })).data;
      setSqftTouched(false);
      if (r.source === "records") {
        setForm((f) => ({ ...f, sqft: r.sqft, bedrooms: r.bedrooms != null ? Math.round(r.bedrooms) : f.bedrooms, bathrooms: r.bathrooms != null ? Math.round(r.bathrooms * 2) / 2 : f.bathrooms }));
      }
      setSize({ key, ...r });
    } catch { setSize({ key, source: "estimate" }); }
  };

  const valid = [
    () => !!service && (!isQuote || (admin && customOk)),
    () => form.line1.trim().length >= 3 && form.city.trim().length >= 2 && /^\d{5}$/.test(form.zip),
    () => true, () => true,
    () => !!hold,
    () => form.name.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.email) && form.phone.replace(/\D/g, "").length >= 10,
    () => true,
  ];
  const next = () => {
    if (!valid[step]()) return toast.error(step === 1 && !/^\d{5}$/.test(form.zip) ? t.book.zipErr : t.book.required);
    if (step === 1) lookupSize();
    setStep(step + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    const body = { ...payload(), hold_id: hold.hold_id, zip: form.zip, property_type: form.property_type,
      contact: { name: form.name, email: form.email, phone: form.phone },
      address: { line1: form.line1, line2: form.line2, city: form.city, zip: form.zip, notes: form.access },
      payment_option: form.payment_option, lang, notes: form.notes };
    try {
      if (admin) {
        const r = await call("post", "/admin/bookings", { ...body, ...(isQuote ? { custom_price: Number(custom.price), custom_hours: Number(custom.hours) } : {}), payment_method: offline.method, amount_received: Number(offline.amount || 0), payment_reference: offline.reference });
        toast.success(`Booking ${r.number} created`);
        navigate("/admin/bookings");
        return;
      }
      const r = await api.post("/bookings", body);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 409) { setHold(null); setStep(4); }
      setSubmitting(false);
    }
  };

  if (!cfg) return <div className="p-20 grid place-items-center"><Loader2 className="animate-spin text-sky-600" /></div>;
  const freqs = service?.allow_recurring ? ["one_time", "weekly", "biweekly", "monthly"] : ["one_time"];
  const fd = cfg.pricing.frequency_discounts || {};

  return (
    <div className={admin ? "" : "bg-slate-50 min-h-screen"}>
      <div className={admin ? "" : "max-w-6xl mx-auto py-10 px-4"}>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">{admin ? "New booking for a customer" : t.book.title}</h1>
        <ol className="mt-6 flex gap-2 overflow-x-auto pb-1" data-testid="wizard-steps">
          {t.book.steps.map((s, i) => (
            <li key={s} className={`shrink-0 flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full ${i === step ? "bg-sky-600 text-white" : i < step ? "bg-sky-100 text-sky-800" : "bg-white text-slate-500 border border-slate-200"}`}>
              {i < step ? <Check size={13} /> : <span>{i + 1}</span>}{s}
            </li>
          ))}
        </ol>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6 fade-up" key={step}>
            {step === 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">{t.book.service}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {fixed.map((s) => (
                    <button key={s.id} type="button" onClick={() => { set("service_id", s.id); if (!s.allow_recurring) set("frequency", "one_time"); }} data-testid={`wizard-service-${s.slug}`}
                      className={`text-left p-4 rounded-2xl border-2 transition-colors ${form.service_id === s.id ? "border-sky-600 bg-sky-50/60" : "border-slate-200 hover:border-sky-300"}`}>
                      <p className="font-semibold text-slate-900">{svcName(s, lang)}</p>
                      <p className="text-sm text-slate-500 mt-1">{s.pricing_model === "fixed" ? `${t.services.from} ${money(Math.max(s.base_price, s.min_price))}` : "Custom price (set below)"}</p>
                    </button>
                  ))}
                </div>
                {admin && isQuote && (
                  <div className="rounded-2xl border-2 border-sky-200 bg-sky-50/50 p-4 grid sm:grid-cols-2 gap-3" data-testid="admin-custom-price-panel">
                    <p className="sm:col-span-2 text-sm text-slate-700">This service has no set price. Enter the price you agreed with the customer and how long the job will take.</p>
                    <div><label className="label">Price $ *</label><input type="number" min={1} className="field" value={custom.price} onChange={(e) => setCustom({ ...custom, price: e.target.value })} data-testid="admin-custom-price" /></div>
                    <div><label className="label">Estimated hours *</label><input type="number" min={1} step={0.5} className="field" value={custom.hours} onChange={(e) => setCustom({ ...custom, hours: e.target.value })} data-testid="admin-custom-hours" /></div>
                  </div>
                )}
                {!admin && <div className="rounded-xl bg-sky-50 border border-sky-100 p-4 text-sm text-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <span>{cfg.services.filter((s) => s.pricing_model === "quote").map((s) => svcName(s, lang)).join(", ")} — {t.book.quoteSvc}</span>
                  <Link to="/quote" className="btn-outline !py-2 text-sm shrink-0" data-testid="wizard-go-quote">{t.book.goQuote}</Link>
                </div>}
              </div>
            )}
            {step === 2 && (
              <div className="space-y-6">
                {admin && isQuote && <p className="text-sm text-slate-500" data-testid="custom-price-note">This job uses the custom price you entered, so home size here is for the crew's information only.</p>}
                <div>
                  <p className="label">{t.book.propertyType}</p>
                  <div className="flex flex-wrap gap-2">
                    {["house", "apartment", "townhouse", "office"].map((p) => (
                      <button key={p} type="button" onClick={() => set("property_type", p)} data-testid={`property-type-${p}`}
                        className={`px-4 py-2 rounded-xl border text-sm font-medium ${form.property_type === p ? "border-sky-600 bg-sky-50 text-sky-800" : "border-slate-200"}`}>{t.book[p]}</button>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-6">
                  <div><p className="label">{t.book.bedrooms}</p><Stepper value={form.bedrooms} onChange={(v) => set("bedrooms", v)} max={10} testid="bedrooms" /></div>
                  <div><p className="label">{t.book.bathrooms}</p><Stepper value={form.bathrooms} onChange={(v) => set("bathrooms", v)} step={0.5} max={10} testid="bathrooms" /></div>
                  <div><p className="label">{t.book.sqft}</p><input type="number" min={0} className="field" value={form.sqft === 0 ? "" : form.sqft} onChange={(e) => { setSqftTouched(true); set("sqft", Math.max(0, Number(e.target.value) || 0)); }} data-testid="sqft-input" /></div>
                  {size && (
                    <p className={`sm:col-span-3 text-sm flex items-center gap-2 ${size.source === "records" && !sqftTouched ? "text-emerald-700" : "text-slate-500"}`} data-testid="sqft-source-note">
                      {size.source === "loading" ? <Loader2 size={14} className="animate-spin" /> : <Ruler size={14} />}
                      {size.source === "loading" ? t.book.sizeLooking : sqftTouched ? t.book.sizeEdited : size.source === "records" ? t.book.sizeFound : t.book.sizeEstimated}
                    </p>
                  )}
                </div>
                <div>
                  <p className="label">{t.book.frequency}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {freqs.map((f) => (
                      <button key={f} type="button" onClick={() => set("frequency", f)} data-testid={`frequency-${f}`}
                        className={`p-3 rounded-xl border text-sm font-medium ${form.frequency === f ? "border-sky-600 bg-sky-50 text-sky-800" : "border-slate-200"}`}>
                        {t.pricing[f]}{fd[f] > 0 && <span className="block text-xs text-emerald-600">-{fd[f]}%</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-3">
                <h2 className="text-xl font-semibold">{t.book.extras}</h2>
                {cfg.addons.map((a) => {
                  const q = form.addons[a.id] || 0;
                  const toggle = () => set("addons", { ...form.addons, [a.id]: q ? 0 : 1 });
                  return (
                    <div key={a.id} className={`flex items-center justify-between p-4 rounded-xl border ${q ? "border-sky-600 bg-sky-50/60" : "border-slate-200"}`}>
                      <button type="button" onClick={toggle} className="flex items-center gap-3 text-left" data-testid={`addon-toggle-${a.id}`}>
                        <span className={`h-5 w-5 shrink-0 rounded-md border grid place-items-center ${q ? "bg-sky-600 border-sky-600 text-white" : "border-slate-300"}`}>{q > 0 && <Check size={14} />}</span>
                        {a.image_url && <img src={imgUrl(a.image_url)} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" data-testid={`addon-thumb-${a.id}`} />}
                        <span className="font-medium text-slate-800">{lang === "es" ? a.name_es : a.name_en}</span>
                      </button>
                      {(a.desc_en || a.desc_es) && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="ml-2 mr-auto p-1 text-slate-400 hover:text-sky-600" data-testid={`addon-info-${a.id}`} aria-label={lang === "es" ? "Qué incluye" : "What's included"}><Info size={16} /></button>
                          </PopoverTrigger>
                          <PopoverContent side="bottom" align="start" collisionPadding={12} className="max-w-xs text-sm" data-testid={`addon-tooltip-${a.id}`}>{a.image_url && <img src={imgUrl(a.image_url)} alt={lang === "es" ? a.name_es : a.name_en} className="mb-2 h-32 w-full rounded-lg object-cover" />}{lang === "es" ? a.desc_es || a.desc_en : a.desc_en || a.desc_es}</PopoverContent>
                        </Popover>
                      )}
                      <div className="flex items-center gap-3">
                        {a.price_type === "per_unit" && q > 0 && <Stepper value={q} min={1} max={20} onChange={(v) => set("addons", { ...form.addons, [a.id]: v })} testid={`addon-qty-${a.id}`} />}
                        <span className="text-sm font-semibold">{money(a.price)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><label className="label">{t.book.address} *</label><input className="field" value={form.line1} onChange={(e) => set("line1", e.target.value)} data-testid="address-line1-input" /></div>
                <div><label className="label">{t.book.line2}</label><input className="field" value={form.line2} onChange={(e) => set("line2", e.target.value)} data-testid="address-line2-input" /></div>
                <div><label className="label">{t.book.city} *</label><input className="field" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="address-city-input" /></div>
                <div><label className="label">{t.book.zip} *</label><input className="field" inputMode="numeric" maxLength={5} value={form.zip} onChange={(e) => set("zip", e.target.value.replace(/\D/g, ""))} data-testid="address-zip-input" /></div>
                <div className="sm:col-span-2"><label className="label">{t.book.accessNotes}</label><textarea rows={2} className="field" value={form.access} onChange={(e) => set("access", e.target.value)} data-testid="address-notes-input" /></div>
              </div>
            )}
            {step === 4 && <SlotPicker hold={hold} onHeld={setHold} hours={price?.estimated_hours} />}
            {step === 5 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><label className="label">{t.book.name} *</label><input className="field" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" /></div>
                <div><label className="label">{t.book.email} *</label><input type="email" className="field" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" /></div>
                <div><label className="label">{t.book.phone} *</label><input type="tel" className="field" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" /></div>
                <div className="sm:col-span-2"><label className="label">{t.book.notes}</label><textarea rows={3} className="field" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="contact-notes" /></div>
              </div>
            )}
            {step === 6 && (
              <div className="space-y-5">
                <h2 className="text-xl font-semibold">{t.book.payOption}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {["full", "deposit"].map((o) => (
                    <button key={o} type="button" onClick={() => set("payment_option", o)} data-testid={`payment-option-${o}`}
                      className={`text-left p-4 rounded-2xl border-2 ${form.payment_option === o ? "border-sky-600 bg-sky-50/60" : "border-slate-200"}`}>
                      <p className="font-semibold">{t.book[o]}</p>
                      <p className="text-2xl font-bold mt-1">{money(o === "full" ? price?.total : price?.deposit_amount)}</p>
                      {o === "deposit" && <p className="text-xs text-slate-500 mt-1">{price?.deposit_percent}% · {t.book.depositNote}</p>}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input className="field uppercase" placeholder={t.book.coupon} value={coupon} onChange={(e) => setCoupon(e.target.value)} data-testid="coupon-input" />
                  <button type="button" className="btn-outline shrink-0" onClick={() => setCouponApplied(coupon.trim())} data-testid="coupon-apply-button">{t.book.apply}</button>
                </div>
                {!(admin && offline.method !== "card_link") && <p className="text-sm text-slate-500 flex items-center gap-2"><Lock size={14} />{t.book.secure}</p>}
                {admin && (
                  <div className="rounded-2xl border-2 border-sky-200 bg-sky-50/50 p-4 space-y-3" data-testid="admin-payment-panel">
                    <p className="font-semibold text-slate-900">Payment collected by staff</p>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div><label className="label">Method</label>
                        <select className="field" value={offline.method} onChange={(e) => setOffline({ ...offline, method: e.target.value })} data-testid="admin-payment-method">
                          <option value="cash">Cash</option><option value="zelle">Zelle</option><option value="check">Check</option><option value="other">Other</option><option value="card_link">Send card payment link to customer</option>
                        </select></div>
                      {offline.method !== "card_link" && <>
                        <div><label className="label">Amount received $</label><div className="flex gap-2"><input type="number" min={0} className="field" placeholder="0" value={offline.amount} onChange={(e) => setOffline({ ...offline, amount: e.target.value })} data-testid="admin-payment-amount" /><button type="button" className="btn-outline !px-3 shrink-0 text-xs" onClick={() => setOffline({ ...offline, amount: String(price?.total ?? "") })} data-testid="admin-payment-full">Paid in full</button></div></div>
                        <div><label className="label">Reference (optional)</label><input className="field" placeholder="Zelle confirmation #" value={offline.reference} onChange={(e) => setOffline({ ...offline, reference: e.target.value })} data-testid="admin-payment-reference" /></div>
                      </>}
                    </div>
                    <p className="text-xs text-slate-500">{offline.method === "card_link" ? "The booking is held and the customer gets an email with a secure link to pay by card." : "The booking is confirmed right away. Leave the amount at 0 if the customer will pay later; the balance stays on the booking."}</p>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-between pt-4 border-t border-slate-100">
              <button type="button" className="btn-outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-step-back-button"><ArrowLeft size={16} />{t.book.back}</button>
              {step < 6 ? (
                <button type="button" className="btn-primary" onClick={next} data-testid="wizard-step-next-button">{t.book.next}<ArrowRight size={16} /></button>
              ) : (
                <button type="button" className="btn-primary" disabled={submitting || !price || loading} onClick={submit} data-testid="wizard-confirm-pay-button">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}{admin ? "Create booking" : t.book.confirm}
                </button>
              )}
            </div>
          </div>
          <Summary price={price} loading={loading} service={service} hold={hold} form={form} />
        </div>
      </div>
    </div>
  );
}
